import React, { useState } from "react";
import styles from "./EditUserModal.module.scss";
import { BaseModal, IModalInstance, ModalHeader } from "@whatslly/ui-components/modals";
import type { UserSupportedLanguageType } from "@whatslly/types/User";
import type { UpdateUserBodyType } from "@whatslly/types/UpdateUserBody";
import { ModalButtons } from "@whatslly/ui-components/ModalButtons";
import { Select } from "@whatslly/ui-components/v2/BaseElements/Select";
import { TextInput } from "@whatslly/ui-components/v2/BaseElements/TextInput";
import type { SelectOption } from "@whatslly/types/UI-V2/BaseElements/SelectType";
import { getPrettyPhone } from "@whatslly/commons/utils";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import { i18n } from "@whatslly/commons/i18nService";
import { getUserSettingLanguageFromI18n } from "@whatslly/commons/LanguageChangeUtils";
import { EditUserWaNotificationSoundSection, useWaNotificationSoundActions } from "./editUserWaNotificationSound";

export type { WaNotificationSoundActions } from "./editUserWaNotificationSound";

interface EditUserModalProps extends IModalInstance<UpdateUserBodyType> {
  user: {
    firstName?: string;
    lastName?: string;
    language?: UserSupportedLanguageType;
    username?: string;
    phone?: string;
    email?: string;
  };
  /** Mirrors `TopBar` / `TopBarWidgetApp` `isDesktop`: Electron shell uses main-process WA notification sound commands. */
  isDesktop?: boolean;
}

const langauges: SelectOption<{ value: UserSupportedLanguageType }>[] = [
  { label: "English", value: "ENGLISH" },
  { label: "Español", value: "SPANISH" },
  { label: "Português", value: "PORTUGUESE" },
];

export const EditUserModal: React.FC<EditUserModalProps> = ({ user, closeModal, isDesktop }) => {
  const { t } = useTranslation();
  const waNotificationSound = useWaNotificationSoundActions({ isDesktop });
  const [firstName, setFirstName] = useState(user.firstName);
  const [lastName, setLastName] = useState(user.lastName);
  const [language, setLanguage] = useState(langauges.find((lang) => lang.value === getUserSettingLanguageFromI18n(i18n.language)) ?? langauges[0]);
  return (
    <BaseModal
      header={<ModalHeader closeModal={closeModal}>{t("edit_user.title")}</ModalHeader>}
      closeModal={closeModal}
      closeOnEscape={false}
      closeOnClickOutside={false}
      footer={
        <ModalButtons
          onCancel={() => closeModal()}
          isSubmitDisabled={!firstName || !lastName}
          testId="submit-edit-user"
          onSubmit={() => closeModal({ firstName, lastName, language: language.value })}
        />
      }
    >
      <div className={styles.editUser} data-testid={"edit-user-modal-content"}>
        <div className={styles.valueSection}>
          <span className={styles.label}>{t("edit_user.username")}</span>
          <div className={classnames(styles.value, styles.valueText)}>{user.username}</div>
        </div>
        {user.phone && (
          <div className={styles.valueSection}>
            <span className={styles.label}>{t("edit_user.phone")}</span>
            <div className={classnames(styles.value, styles.valueText)}>{getPrettyPhone(user.phone)}</div>
          </div>
        )}
        {user.email && (
          <div className={styles.valueSection}>
            <span className={styles.label}>{t("edit_user.email")}</span>
            <div className={classnames(styles.value, styles.valueText)}>{user.email}</div>
          </div>
        )}
        <div className={styles.editSection}>
          <span className={styles.label}>{t("edit_user.first_name")}</span>
          <div className={styles.value}>
            <TextInput
              type="text"
              value={firstName}
              containerClassName={styles.input}
              removeClear={true}
              data-testid="user-first-name"
              onChange={(e) => setFirstName(e.target.value)}
            />
          </div>
        </div>
        <div className={styles.editSection}>
          <span className={styles.label}>{t("edit_user.last_name")}</span>
          <div className={styles.value}>
            <TextInput
              type="text"
              value={lastName}
              containerClassName={styles.input}
              removeClear={true}
              data-testid="user-last-name"
              onChange={(e) => setLastName(e.target.value)}
            />
          </div>
        </div>
        <EditUserWaNotificationSoundSection sound={waNotificationSound} />
        <div className={styles.editSection}>
          <span className={styles.label}>{t("lang_switch.language")}</span>
          <div className={styles.value}>
            <Select<SelectOption<{ value: UserSupportedLanguageType }>>
              paddingInline={"none"}
              name={"calendarType"}
              value={language}
              data-testid="user-language"
              onChange={setLanguage}
              options={langauges}
            />
          </div>
        </div>
      </div>
    </BaseModal>
  );
};
